import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { fetchJson } from '../lib/http';
import { formatMoney, formatDate } from '../lib/format';
import { UpdateContactModal } from '../components/UpdateContactModal';

interface Profile {
	id: string;
	first_name: string;
	last_name: string;
	phone: string | null;
	tax_exempt: boolean;
	billing_addr_line1: string | null;
	billing_addr_city: string | null;
	billing_addr_state: string | null;
	billing_addr_zip: string | null;
}

interface Financial {
	outstanding_balance: number;
	current_balance: number;
	open_invoices_balance: number;
	starting_balance: number;
	credit_balance: number;
}

interface Invoice {
	id: string;
	invoice_number: string;
	issue_date: string;
	status: string;
	sub_total: number;
	tax_total: number;
	balance_due: number;
}

interface Payment {
	id: string;
	amount: number;
	date: string;
	method: string | null;
	reference: string | null;
	memo: string | null;
	status: string | null;
}

export function DashboardPage() {
	const [profile, setProfile] = useState<Profile | null>(null);
	const [financial, setFinancial] = useState<Financial | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showInvoices, setShowInvoices] = useState(false);
	const [showPayments, setShowPayments] = useState(false);
	const [invoices, setInvoices] = useState<Invoice[] | null>(null);
	const [payments, setPayments] = useState<Payment[] | null>(null);
	const [appsLoading, setAppsLoading] = useState({ invoices: false, payments: false });
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [appsByInvoice, setAppsByInvoice] = useState<Record<string, any[]>>({});
	const [appsLoadingById, setAppsLoadingById] = useState<Record<string, boolean>>({});
	const [appsErrorById, setAppsErrorById] = useState<Record<string, string>>({});
	const [modalOpen, setModalOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			setError('');
			try {
				const data = await fetchJson<{ profile: Profile; financial: Financial }>('/portal/customer');
				if (!cancelled) { setProfile(data.profile); setFinancial(data.financial); }
			} catch (_e) {
				if (!cancelled) setError('Failed to load dashboard.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (showInvoices && invoices === null && !appsLoading.invoices) {
			setAppsLoading(p => ({ ...p, invoices: true }));
			fetchJson<Invoice[]>('/portal/invoices')
				.then(data => setInvoices(data))
				.catch(() => {/* handled via UI state */})
				.finally(() => setAppsLoading(p => ({ ...p, invoices: false })));
		}
	}, [showInvoices, invoices, appsLoading.invoices]);

	useEffect(() => {
		if (showPayments && payments === null && !appsLoading.payments) {
			setAppsLoading(p => ({ ...p, payments: true }));
			fetchJson<Payment[]>('/portal/payments')
				.then(data => setPayments(data))
				.catch(() => {/* handled via UI state */})
				.finally(() => setAppsLoading(p => ({ ...p, payments: false })));
		}
	}, [showPayments, payments, appsLoading.payments]);

	async function toggleInvoiceExpand(id: string) {
		setExpandedId(prev => (prev === id ? null : id));
		if (!appsByInvoice[id] && !appsLoadingById[id]) {
			setAppsLoadingById(prev => ({ ...prev, [id]: true }));
			setAppsErrorById(prev => ({ ...prev, [id]: '' }));
			try {
				const data = await fetchJson<any[]>(`/portal/invoices/${id}/applications`);
				setAppsByInvoice(prev => ({ ...prev, [id]: data }));
			} catch (_e) {
				setAppsErrorById(prev => ({ ...prev, [id]: 'Failed to load payments for this invoice.' }));
			} finally {
				setAppsLoadingById(prev => ({ ...prev, [id]: false }));
			}
		}
	}

	return (
		<Layout>
			<div className="space-y-8">
				{/* Financial Overview */}
				<div className="rounded-2xl border border-gray-200 bg-white elev-1">
					<div className="p-5 border-b border-gray-100">
						<h1 className="text-2xl font-extrabold text-gray-900">Dashboard</h1>
					</div>
					{loading ? (
						<div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							{Array.from({ length: 4 }).map((_, i) => (
								<div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
							))}
						</div>
					) : error ? (
						<div className="p-6 text-sm text-red-700">{error}</div>
					) : (
						<div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							<div className="rounded-xl border border-gray-100 p-4">
								<div className="text-xs uppercase tracking-wide text-gray-600">Outstanding balance</div>
								<div className="mt-1 text-2xl font-extrabold text-gray-900">{formatMoney(financial?.outstanding_balance || 0)}</div>
							</div>
							<div className="rounded-xl border border-gray-100 p-4">
								<div className="text-xs uppercase tracking-wide text-gray-600">Current balance</div>
								<div className="mt-1 text-2xl font-extrabold text-gray-900">{formatMoney(financial?.current_balance || 0)}</div>
							</div>
							<div className="rounded-xl border border-gray-100 p-4">
								<div className="text-xs uppercase tracking-wide text-gray-600">Open invoices</div>
								<div className="mt-1 text-xl font-bold text-gray-900">{formatMoney(financial?.open_invoices_balance || 0)}</div>
							</div>
							<div className="rounded-xl border border-gray-100 p-4">
								<div className="text-xs uppercase tracking-wide text-gray-600">Credit balance</div>
								<div className="mt-1 text-xl font-bold text-gray-900">{formatMoney(financial?.credit_balance || 0)}</div>
							</div>
						</div>
					)}
				</div>

				{/* Contact information */}
				<div className="rounded-2xl border border-gray-200 bg-white elev-1">
					<div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3">
						<h2 className="text-xl font-extrabold text-gray-900">Contact information</h2>
						<button onClick={() => setModalOpen(true)} className="ml-auto rounded-xl border border-sedra text-sedra px-4 py-2 text-sm font-semibold hover:bg-sedra/5 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">
							Update information
						</button>
					</div>
					{loading ? (
						<div className="p-5 space-y-2">
							{Array.from({ length: 6 }).map((_, i) => (
								<div key={i} className="h-5 rounded-lg bg-gray-100 animate-pulse w-1/2" />
							))}
						</div>
					) : profile ? (
						<div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-800">
							<div><span className="font-semibold text-gray-700">First name:</span> {profile.first_name || '-'}</div>
							<div><span className="font-semibold text-gray-700">Last name:</span> {profile.last_name || '-'}</div>
							<div><span className="font-semibold text-gray-700">Customer ID:</span> {profile.id}</div>
							<div><span className="font-semibold text-gray-700">Phone:</span> {profile.phone || '-'}</div>
							<div><span className="font-semibold text-gray-700">Tax exemption:</span> {profile.tax_exempt ? 'Yes' : 'No'}</div>
							<div><span className="font-semibold text-gray-700">Billing address:</span> {profile.billing_addr_line1 || '-'}</div>
							<div><span className="font-semibold text-gray-700">City:</span> {profile.billing_addr_city || '-'}</div>
							<div><span className="font-semibold text-gray-700">State:</span> {profile.billing_addr_state || '-'}</div>
							<div><span className="font-semibold text-gray-700">Zip:</span> {profile.billing_addr_zip || '-'}</div>
						</div>
					) : null}
				</div>

				<UpdateContactModal
					open={modalOpen}
					onClose={() => setModalOpen(false)}
					initial={profile || undefined}
				/>

				{/* Collapsible: Invoices */}
				<div className="rounded-2xl border border-gray-200 bg-white elev-1">
					<button
						className="w-full text-left px-5 py-4 border-b border-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
						aria-expanded={showInvoices}
						aria-controls="section-invoices"
						onClick={() => setShowInvoices(v => !v)}
					>
						<span className="text-lg font-extrabold text-gray-900">Invoices</span>
					</button>
					<div id="section-invoices" hidden={!showInvoices} className="max-h-[70vh] overflow-auto">
						{!showInvoices ? null : invoices === null ? (
							<div className="p-5 space-y-2">
								{Array.from({ length: 6 }).map((_, i) => (
									<div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />
								))}
							</div>
						) : invoices.length === 0 ? (
							<div className="p-6 text-sm text-gray-700">No invoices</div>
						) : (
							<table className="min-w-full">
								<thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur">
									<tr>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Invoice #</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Issued</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Subtotal</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Tax</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Balance due</th>
										<th className="px-4 py-2" />
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-100">
									{invoices.map(inv => (
										<React.Fragment key={inv.id}>
										<tr className="odd:bg-white even:bg-gray-50/40">
											<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{inv.invoice_number}</td>
											<td className="px-4 py-3 text-sm text-gray-700">{formatDate(inv.issue_date)}</td>
											<td className="px-4 py-3 text-sm text-gray-700">{inv.status}</td>
											<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{formatMoney(inv.sub_total)}</td>
											<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{formatMoney(inv.tax_total)}</td>
											<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{formatMoney(inv.balance_due)}</td>
											<td className="px-4 py-3 text-right">
												<button onClick={() => toggleInvoiceExpand(inv.id)} aria-expanded={expandedId === inv.id} className="rounded-xl border border-sedra text-sedra px-3 py-1.5 text-sm font-semibold hover:bg-sedra/5 focus-visible:ring-2 focus-visible:ring-sedra focus-visible:ring-offset-2">
													{expandedId === inv.id ? 'Hide payments' : 'Show payments'}
												</button>
											</td>
										</tr>
										{expandedId === inv.id && (
											<tr>
												<td colSpan={7} className="px-4 pb-4">
													<div className="rounded-xl border border-gray-100 bg-gray-50 p-4 mt-1">
														<div className="flex items-center justify-between mb-2">
															<div className="text-sm font-semibold text-gray-800">Payments applied</div>
															<button onClick={() => toggleInvoiceExpand(inv.id)} className="text-xs text-sedra hover:underline">Hide</button>
														</div>
														{appsLoadingById[inv.id] && <div className="text-sm text-gray-600">Loading…</div>}
														{appsErrorById[inv.id] && <div className="text-sm text-red-700">{appsErrorById[inv.id]}</div>}
														{!appsLoadingById[inv.id] && !appsErrorById[inv.id] && (
															appsByInvoice[inv.id]?.length ? (
																<ul className="space-y-2">
																	{appsByInvoice[inv.id].map((a: any) => (
																		<li key={`${a.payment_id}-${a.invoice_id}`} className="text-sm text-gray-800">
																			<span className="font-medium">{formatDate(a.date)}</span>
																			<span className="mx-2">—</span>
																			<span>{formatMoney(Number(a.applied_amount))} of {formatMoney(Number(a.payment_amount))}</span>
																			{a.method ? <span className="ml-2 text-gray-600">({a.method}{a.reference ? ` • ${a.reference}` : ''})</span> : null}
																		</li>
																	))}
																</ul>
															) : (
																<div className="text-sm text-gray-600">No payments applied yet.</div>
															)
														)}
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>

				{/* Collapsible: Payments */}
				<div className="rounded-2xl border border-gray-200 bg-white elev-1">
					<button
						className="w-full text-left px-5 py-4 border-b border-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
						aria-expanded={showPayments}
						aria-controls="section-payments"
						onClick={() => setShowPayments(v => !v)}
					>
						<span className="text-lg font-extrabold text-gray-900">Payments</span>
					</button>
					<div id="section-payments" hidden={!showPayments} className="max-h-[70vh] overflow-auto">
						{!showPayments ? null : payments === null ? (
							<div className="p-5 space-y-2">
								{Array.from({ length: 6 }).map((_, i) => (
									<div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />
								))}
							</div>
						) : payments.length === 0 ? (
							<div className="p-6 text-sm text-gray-700">No payments</div>
						) : (
							<table className="min-w-full">
								<thead className="sticky top-0 z-10 bg-gray-50/80 backdrop-blur">
									<tr>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Date</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Amount</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Method</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Reference</th>
										<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-100">
									{payments.map(p => (
										<tr key={p.id} className="odd:bg-white even:bg-gray-50/40">
											<td className="px-4 py-3 text-sm text-gray-700">{formatDate(p.date)}</td>
											<td className="px-4 py-3 text-sm text-gray-900 font-semibold">{formatMoney(p.amount)}</td>
											<td className="px-4 py-3 text-sm text-gray-700">{p.method || '-'}</td>
											<td className="px-4 py-3 text-sm text-gray-700">{p.reference || '-'}</td>
											<td className="px-4 py-3 text-sm text-gray-700">{p.status || '-'}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>
			</div>
		</Layout>
	);
}

export default DashboardPage; 